import { motion } from "framer-motion";
import { Bot } from "lucide-react";
import { APPS } from "@/data/apps";
import { useExecution } from "@/store/executionStore";
import { agentRunsFromExecutions } from "@/lib/agents";
import {
  AppHeader,
  Badge,
  Card,
  Progress,
  SectionTitle,
} from "@/components/ui/primitives";

const accent = APPS.agents.accent;

export function AgentsApp() {
  const executions = useExecution((s) => s.executions);
  const runs = agentRunsFromExecutions(executions);
  const running = runs.filter((a) => a.status === "running");
  const completed = executions.filter((e) => e.status === "completed").length;
  const failed = executions.filter((e) => e.status === "failed").length;

  return (
    <div className="min-h-full">
      <AppHeader
        icon={Bot}
        title="Agent Fleet"
        subtitle="Agents working your executions right now"
        accent={accent}
      />

      <div className="grid grid-cols-4 gap-3 px-6 pt-6">
        {[
          { label: "Active", value: running.length },
          { label: "Queued", value: runs.filter((a) => a.status === "queued").length },
          { label: "Completed runs", value: completed },
          { label: "Failed runs", value: failed },
        ].map((s) => (
          <Card key={s.label} className="p-4">
            <div className="text-[11px] uppercase tracking-wider text-app-2">{s.label}</div>
            <div className="mt-1 text-2xl font-semibold text-app tabular-nums">{s.value}</div>
          </Card>
        ))}
      </div>

      <div className="p-6">
        <SectionTitle>Fleet</SectionTitle>
        {runs.length === 0 ? (
          <Card className="p-8 text-center">
            <div className="text-sm font-semibold text-app">No agents active</div>
            <p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-app-2">
              Agents appear here while executions are in flight. Run a workflow — or a
              sandbox test from the Workflow Builder — to see the fleet at work.
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {runs.map((a, i) => {
              const app = APPS[a.app];
              return (
                <motion.div
                  key={a.id}
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Card className="p-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="grid h-10 w-10 place-items-center rounded-xl text-white"
                        style={{ background: `linear-gradient(135deg, ${app.accent}, ${app.accent}aa)` }}
                      >
                        <app.icon size={18} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-app">{a.name}</span>
                          <Badge tone={a.status}>{a.status}</Badge>
                        </div>
                        <div className="text-xs text-app-2">{app.name} · started {a.startedAt}</div>
                      </div>
                    </div>
                    <div className="mt-3 truncate text-xs text-app-1">{a.task}</div>
                    {a.status === "running" && (
                      <div className="mt-2 flex items-center gap-2">
                        <Progress value={a.progress} color={app.accent} />
                        <span className="text-[10px] tabular-nums text-app-2">
                          {Math.round(a.progress * 100)}%
                        </span>
                      </div>
                    )}
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
